import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import type { IntlShape } from 'react-intl'
import { FlightsClassTypes } from 'constants/flight'
import { getSkyLoungeLabel } from 'lib/flights/sharedFlightLabels'

export function getCharterFareCabinLabel(intl: IntlShape, classType: FlightsClassTypes): string {
  switch (classType) {
    case FlightsClassTypes.ECONOMY:
      return intl.formatMessage({
        id: 'K7cg5KkP69KFGlsuL7K5',
        defaultMessage: 'Economy',
        description: 'Charter flight cabin class label for economy in the flights flow',
      })
    case FlightsClassTypes.PREMIUM:
      return intl.formatMessage({
        id: 'mhY8-lGm2cKyrBAYbX5n',
        defaultMessage: 'Economy Plus',
        description: 'Charter flight cabin class label for economy plus (premium class on charter flights)',
      })
    case FlightsClassTypes.BUSINESS:
      return intl.formatMessage({
        id: 'khVhC_vM-jEVuFUq7pvL',
        defaultMessage: 'Business',
        description: 'Charter flight cabin class label for business class',
      })
    case FlightsClassTypes.FIRST:
      return intl.formatMessage({
        id: 'DOx8fDJAhNsGpB_-k-h9',
        defaultMessage: 'First',
        description: 'Charter flight cabin class label for first class in the flights section',
      })
    case FlightsClassTypes.LUXROW:
    case FlightsClassTypes.LUXROW_PAX_ONE:
      return getSkyLoungeLabel(intl)
  }
}

export function getCharterBundleDurationLabel(intl: IntlShape, nights: number): string {
  return intl.formatMessage(
    {
      id: 'UWTJCFgohv6ChMra2PiX',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights',
      description: 'Charter bundle duration option label (e.g. "5 nights & flights")',
    },
    { nights },
  )
}

export function getMaldivesBundleLabel(intl: IntlShape): string {
  return intl.formatMessage({
    id: 'f1zpGr0GnPcauF3M9U2F',
    defaultMessage: 'Hotel & Flight bundle',
    description: 'Label for Maldives hotel and flight bundle package',
  })
}

function useCharterFlightLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const charterFareCabinLabelMap: Record<FlightsClassTypes, string> = {
      [FlightsClassTypes.ECONOMY]: getCharterFareCabinLabel(intl, FlightsClassTypes.ECONOMY),
      [FlightsClassTypes.PREMIUM]: getCharterFareCabinLabel(intl, FlightsClassTypes.PREMIUM),
      [FlightsClassTypes.BUSINESS]: getCharterFareCabinLabel(intl, FlightsClassTypes.BUSINESS),
      [FlightsClassTypes.FIRST]: getCharterFareCabinLabel(intl, FlightsClassTypes.FIRST),
      [FlightsClassTypes.LUXROW]: getCharterFareCabinLabel(intl, FlightsClassTypes.LUXROW),
      [FlightsClassTypes.LUXROW_PAX_ONE]: getCharterFareCabinLabel(intl, FlightsClassTypes.LUXROW_PAX_ONE),
    }

    const charterBundleDurationOptions = [
      { value: 5 as const, label: getCharterBundleDurationLabel(intl, 5) },
      { value: 12 as const, label: getCharterBundleDurationLabel(intl, 12) },
    ]

    const fareClassOptions = [
      { value: FlightsClassTypes.ECONOMY, label: charterFareCabinLabelMap[FlightsClassTypes.ECONOMY] },
      { value: FlightsClassTypes.PREMIUM, label: charterFareCabinLabelMap[FlightsClassTypes.PREMIUM] },
      { value: FlightsClassTypes.LUXROW, label: charterFareCabinLabelMap[FlightsClassTypes.LUXROW] },
      { value: FlightsClassTypes.BUSINESS, label: charterFareCabinLabelMap[FlightsClassTypes.BUSINESS] },
    ]

    const maldivesBundleLabel = getMaldivesBundleLabel(intl)

    return {
      charterFareCabinLabelMap,
      charterBundleDurationOptions,
      fareClassOptions,
      maldivesBundleLabel,
    }
  }, [intl])
}

export default useCharterFlightLabels
